import tiktokAuth from './tiktokAuth';

class TikTokAPI {
  constructor() {
    this.baseUrl = 'https://toldfel.hu/tiktok';
  }

  // Get creator information (required before posting)
  async getCreatorInfo() {
    const accessToken = await tiktokAuth.getValidAccessToken();
    if (!accessToken) {
      throw new Error('Not authenticated with TikTok. Please reconnect your TikTok account.');
    }

    try {
      const response = await fetch(`${this.baseUrl}/creator-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: accessToken
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        // Check if it's an auth error that might be fixed with a reconnect
        const errorMsg = error.error || 'Failed to get creator info';
        if (response.status === 401 || errorMsg.includes('token') || errorMsg.includes('auth')) {
          throw new Error('TikTok authentication expired. Please reconnect your TikTok account.');
        }
        throw new Error(errorMsg);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('TikTok creator info error:', error);
      throw error;
    }
  }

  // Upload video to TikTok
  async uploadVideo(videoUrl, title, description, options = {}) {
    const accessToken = await tiktokAuth.getValidAccessToken();
    if (!accessToken) {
      throw new Error('Not authenticated with TikTok. Please reconnect your TikTok account.');
    }

    const token = tiktokAuth.getToken();

    const {
      privacy_level = 'SELF_ONLY',
      disable_comment = false,
      disable_duet = false,
      disable_stitch = false,
      brand_content_toggle = false,
      brand_organic_toggle = false,
      video_cover_timestamp_ms
    } = options;

    try {
      const response = await fetch(`${this.baseUrl}/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: accessToken,
          open_id: token.open_id,
          video_url: videoUrl,
          title: title,
          description: description,
          privacy_level,
          disable_comment,
          disable_duet,
          disable_stitch,
          brand_content_toggle,
          brand_organic_toggle,
          video_cover_timestamp_ms
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        const errorMsg = error.error || 'Failed to upload to TikTok';
        // Check if it's an auth error
        if (response.status === 401 || errorMsg.includes('token') || errorMsg.includes('auth')) {
          throw new Error('TikTok authentication expired. Please reconnect your TikTok account in the Connections tab.');
        }
        throw new Error(errorMsg);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('TikTok upload error:', error);
      throw error;
    }
  }

  // Check upload status
  async checkPublishStatus(publishId) {
    const accessToken = await tiktokAuth.getValidAccessToken();
    if (!accessToken) {
      throw new Error('Not authenticated with TikTok');
    }

    try {
      const response = await fetch(`${this.baseUrl}/publish-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: accessToken,
          publish_id: publishId
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to check publish status');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('TikTok status check error:', error);
      throw error;
    }
  }
}

const tiktokAPI = new TikTokAPI();

export default tiktokAPI;
